import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";

import { useHasRole } from "../../../hooks/useHasRole";
import { getStationChargers } from "../../../services/stations";
import {
  getSubscriptions,
  messageForError,
  setSubscription,
  stateForError,
} from "../../../services/notificationSettings";
import { PanelState, StationSubscription } from "../../../types/notificationSettings";

/** The raw OCPI vocabulary the subscription store speaks — not the derived alerting statuses. */
const OCPI_STATUSES = [
  "AVAILABLE",
  "BLOCKED",
  "CHARGING",
  "INOPERATIVE",
  "OUTOFORDER",
  "PLANNED",
  "REMOVED",
  "RESERVED",
  "UNKNOWN",
];

const parseEvses = (raw: string) => raw.split(",").map((e) => e.trim()).filter(Boolean);

/**
 * A station together with a charger selection: the pair the service treats as one unit. Writing to
 * one scope leaves the station's other scopes alone, so the form and the rows agree on what a
 * change will touch.
 */
const scopeOf = (stationId: string, evses: string[]) =>
  `${stationId}|${[...evses].sort().join(",")}`;

/** Distinct per row: one station can appear once per charger selection. */
const rowKey = (s: StationSubscription) =>
  s.evses.length ? scopeOf(s.stationId, s.evses) : s.stationId;

const describeScope = (stationId: string, evses: string[]) =>
  evses.length ? `${stationId} · ${evses.join(", ")}` : `${stationId} · the whole station`;

const StationSubscriptionsPanel = ({ userId }: { userId: string }) => {
  const canEdit = useHasRole("admin");
  const [state, setState] = useState<PanelState>("loading");
  const [rows, setRows] = useState<StationSubscription[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<StationSubscription | null>(null);
  const [newStation, setNewStation] = useState("");
  const [newEvses, setNewEvses] = useState("");
  const [picked, setPicked] = useState<{ scope: string; statuses: string[] } | null>(null);
  const [known, setKnown] = useState<{ station: string; chargers: string[] | null } | null>(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    setState("loading");
    setError(null);
    try {
      const subs = await getSubscriptions(userId);
      setRows(subs);
      setState(subs.length ? "ready" : "empty");
    } catch (e) {
      setError(messageForError(e));
      setState(stateForError(e));
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const station = newStation.trim();
  const evses = parseEvses(newEvses);
  const scope = scopeOf(station, evses);

  /**
   * Station-ops is the register for stations and chargers, so an identifier is checked against it
   * before anything is written. Typing one that does not exist would otherwise store a
   * subscription that can never match an event, and leave an audit entry claiming it exists.
   */
  useEffect(() => {
    if (!station) {
      setKnown(null);
      return;
    }
    let current = true;
    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const chargers = await getStationChargers(station);
        if (current) setKnown({ station, chargers });
      } catch {
        // Unreachable is not the same as unknown: say nothing rather than block on a blip.
        if (current) setKnown(null);
      } finally {
        if (current) setChecking(false);
      }
    }, 400);
    return () => {
      current = false;
      clearTimeout(timer);
      setChecking(false);
    };
  }, [station]);

  const stationUnknown = known?.station === station && known.chargers === null;
  const chargerOptions = useMemo(() => {
    const fromRegister = known?.station === station ? known.chargers ?? [] : [];
    // Chargers a person is already subscribed to stay pickable even once the station drops them.
    const held = rows.filter((r) => r.stationId === station).flatMap((r) => r.evses);
    return Array.from(new Set([...fromRegister, ...held, ...evses])).sort();
  }, [known, station, rows, evses]);
  const unknownChargers = evses.filter(
    (e) => known?.station === station && known.chargers !== null && !known.chargers.includes(e),
  );

  /** One station can hold several rows, so the table is keyed by the scope rather than the station. */
  const tableRows = useMemo(() => rows.map((r) => ({ ...r, rowKey: rowKey(r) })), [rows]);

  /** The row the form currently points at, if the person already holds one for this exact scope. */
  const editing = useMemo(
    () => rows.find((r) => scopeOf(r.stationId, r.evses) === scope) ?? null,
    [rows, scope],
  );

  /**
   * What is already held shows ticked the moment the form points at an existing subscription, so
   * adding one status is one click rather than re-picking the set. Derived rather than copied into
   * state on a change: a copy lands a render late, and the save button is live in between. Picks
   * survive until the scope itself moves, which is when they stop meaning anything.
   */
  const newStatuses = picked?.scope === scope ? picked.statuses : editing?.statuses ?? [];

  const save = async (
    stationId: string,
    chargers: string[],
    statuses: string[],
    after: () => void,
  ) => {
    setBusy(true);
    setWriteError(null);
    try {
      await setSubscription(userId, stationId, chargers, statuses);
      after();
      await load();
    } catch (e) {
      setWriteError(messageForError(e));
    } finally {
      setBusy(false);
    }
  };

  /** Removal is the same declarative write with nothing ticked, so it drops exactly this row. */
  const remove = (row: StationSubscription) =>
    save(row.stationId, row.evses, [], () => setPending(null));

  const submit = () => {
    if (!station || stationUnknown || unknownChargers.length) return;
    if (!editing && newStatuses.length === 0) return;
    void save(station, evses, newStatuses, () => {
      setNewStation("");
      setNewEvses("");
      setPicked(null);
    });
  };

  const hint = () => {
    if (!station) return "Pick a station first; leaving the chargers empty covers all of them.";
    if (checking) return `Checking ${station} against the station register…`;
    if (stationUnknown) return `station-ops knows no station called ${station}.`;
    if (unknownChargers.length) {
      return `${unknownChargers.join(", ")} is not a charger at ${station}.`;
    }
    if (editing && newStatuses.length === 0) {
      return `Saving with nothing ticked removes ${describeScope(station, evses)}.`;
    }
    if (editing) {
      return `Replacing the statuses held for ${describeScope(station, evses)}.`;
    }
    if (evses.length) {
      return `Adding ${describeScope(station, evses)}. Other chargers of this station are untouched.`;
    }
    return `The whole station: saving replaces everything this person holds for ${station}.`;
  };

  const addForm = canEdit ? (
    <form
      className="flex flex-column gap-2 mt-3 pt-3 border-top-1 surface-border"
      data-testid="add-subscription"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="flex align-items-end gap-2 flex-wrap">
        <div className="flex flex-column gap-2">
          <label htmlFor="new-station" className="text-sm">Station</label>
          <InputText
            id="new-station"
            value={newStation}
            onChange={(e) => setNewStation(e.target.value)}
            placeholder="DECOCMALAGA"
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="new-evses" className="text-sm">Chargers (optional)</label>
          <MultiSelect
            inputId="new-evses"
            data-testid="new-evses"
            value={evses}
            options={chargerOptions.map((v) => ({ label: v, value: v }))}
            onChange={(e) => setNewEvses((e.value as string[]).join(", "))}
            placeholder={station ? "none = whole station" : "pick a station first"}
            disabled={!station || stationUnknown}
            className="w-18rem"
            emptyMessage="This station has no chargers on file"
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="new-statuses" className="text-sm">Statuses</label>
          <MultiSelect
            inputId="new-statuses"
            data-testid="new-statuses"
            value={newStatuses}
            options={OCPI_STATUSES.map((v) => ({ label: v, value: v }))}
            onChange={(e) => setPicked({ scope, statuses: e.value })}
            placeholder="Pick statuses"
            className="w-18rem"
          />
        </div>
        <Button
          type="submit"
          label={editing ? "Save changes" : "Add subscription"}
          icon={editing ? "pi pi-check" : "pi pi-plus"}
          size="small"
          disabled={
            busy || checking || !station || stationUnknown || (!editing && newStatuses.length === 0)
          }
        />
      </div>
      <small className="text-color-secondary" data-testid="scope-hint">{hint()}</small>
    </form>
  ) : null;

  if (state === "loading") {
    return (
      <div className="flex align-items-center gap-3 py-4" data-testid="subscriptions-loading">
        <ProgressSpinner style={{ width: "2rem", height: "2rem" }} strokeWidth="4" />
        <span className="text-color-secondary">Loading subscriptions…</span>
      </div>
    );
  }

  if (state === "refused") {
    return (
      <div className="py-3" data-testid="subscriptions-refused">
        <Message severity="warn" text="Viewing another person's subscriptions requires an administrator entitlement." />
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div className="py-3" data-testid="subscriptions-failed">
        <Message severity="error" text={`Could not load subscriptions${error ? `: ${error}` : ""}`} />
      </div>
    );
  }

  if (state === "empty") {
    return (
      <div className="py-3" data-testid="subscriptions-empty">
        <Message severity="info" text="This person has not subscribed to any station." />
        {addForm}
        {writeError && (
          <div className="mt-3" data-testid="subscriptions-write-error">
            <Message severity="error" text={`Could not save: ${writeError}`} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="subscriptions-ready">
      <DataTable value={tableRows} dataKey="rowKey" paginator={false} className="p-datatable-sm">
        <Column field="stationId" header="Station" />
        <Column
          header="Chargers"
          body={(s: StationSubscription) =>
            s.evses.length ? s.evses.join(", ") : <span className="text-color-secondary">whole station</span>
          }
        />
        <Column header="Statuses" body={(s: StationSubscription) => s.statuses.join(", ")} />
        {canEdit ? (
          <Column
            header=""
            body={(s: StationSubscription) => (
              <div className="flex gap-2 justify-content-end">
                <Button
                  data-testid={`edit-${rowKey(s)}`}
                  label="Edit"
                  icon="pi pi-pencil"
                  severity="secondary"
                  text
                  size="small"
                  disabled={busy}
                  onClick={() => {
                    setNewStation(s.stationId);
                    setNewEvses(s.evses.join(", "));
                  }}
                />
                <Button
                  data-testid={`remove-${rowKey(s)}`}
                  label="Remove"
                  icon="pi pi-times"
                  severity="secondary"
                  outlined
                  size="small"
                  disabled={busy}
                  onClick={() => setPending(s)}
                />
              </div>
            )}
          />
        ) : (
          <Column header="" body={() => null} />
        )}
      </DataTable>

      {pending && (
        <div className="flex align-items-center gap-3 mt-3 p-3 border-round surface-100" data-testid="remove-confirm">
          <span>
            Stop telling this person about <strong>{describeScope(pending.stationId, pending.evses)}</strong>?
            They asked for these messages themselves.
          </span>
          <Button
            data-testid="confirm-remove"
            label="Remove"
            severity="danger"
            size="small"
            disabled={busy}
            onClick={() => void remove(pending)}
          />
          <Button label="Cancel" size="small" text disabled={busy} onClick={() => setPending(null)} />
        </div>
      )}

      {addForm}

      {writeError && (
        <div className="mt-3" data-testid="subscriptions-write-error">
          <Message severity="error" text={`Could not save: ${writeError}`} />
        </div>
      )}
    </div>
  );
};

export default StationSubscriptionsPanel;
